import * as Effect from "effect/Effect";
import * as Schema from "effect/Schema";
import { IsoDateTime, TrimmedNonEmptyString, TrimmedString } from "./baseSchemas.ts";

// ── MCP Gateway (self-hosted Docker MCP Gateway + admin sidecar) ─────────
//
// The gateway aggregates third-party MCP servers ("plugins": Notion, Slack,
// Atlassian, Gmail, …) behind one streamable-HTTP endpoint the coding agents
// consume with a static bearer token. It has no admin API; the server manages
// its config files through the admin sidecar (see infra/mcp-gateway/).
// Credentials never appear in these contracts beyond write-only RPC inputs —
// the server stores them in its secret store and renders the gateway's
// secrets file from there.

export const McpGatewayPluginId = Schema.Literals(["notion", "slack", "atlassian", "gmail"]);
export type McpGatewayPluginId = typeof McpGatewayPluginId.Type;

/**
 * Persisted per-plugin state (lives in `ServerSettings.mcpGateway.plugins`).
 * The registry/config files pushed to the gateway are pure projections of
 * this plus the secret store — the server never parses gateway files back.
 */
export const McpGatewayPluginSettings = Schema.Struct({
  enabled: Schema.Boolean,
  /** Non-secret config values keyed by descriptor `configFields` keys. */
  config: Schema.Record(Schema.String, Schema.String),
  /**
   * Which credential path activated this plugin: "oauth" (web consent flow)
   * or "manual" (pasted tokens). Absent for pre-OAuth persisted settings —
   * treated as "manual".
   */
  authMethod: Schema.optional(Schema.Literals(["oauth", "manual"])),
});
export type McpGatewayPluginSettings = typeof McpGatewayPluginSettings.Type;

/**
 * Describes one input the user must provide to activate a plugin. Rendered
 * generically by the web UI; the server owns the mapping to gateway secret
 * and config keys.
 */
export const McpGatewayFieldSpec = Schema.Struct({
  key: TrimmedNonEmptyString,
  label: Schema.String,
  control: Schema.Literals(["text", "password"]),
  placeholder: Schema.optional(Schema.String),
  helpText: Schema.optional(Schema.String),
  optional: Schema.optional(Schema.Boolean),
});
export type McpGatewayFieldSpec = typeof McpGatewayFieldSpec.Type;

/**
 * How a plugin obtains credentials: "secrets" = user-pasted tokens;
 * "oauth-remote" = web OAuth against the connector's remote MCP server
 * (DCR + PKCE, run by the t3code server), with the secrets form kept as a
 * manual fallback.
 */
export const McpGatewayAuthMode = Schema.Literals(["secrets", "oauth-remote"]);
export type McpGatewayAuthMode = typeof McpGatewayAuthMode.Type;

export const McpGatewayPluginDescriptor = Schema.Struct({
  pluginId: McpGatewayPluginId,
  /** Server name in the gateway's catalog/registry (e.g. "gmail-mcp"). */
  serverName: TrimmedNonEmptyString,
  displayName: Schema.String,
  description: Schema.String,
  docsUrl: Schema.String,
  authMode: McpGatewayAuthMode.pipe(Schema.withDecodingDefault(Effect.succeed("secrets" as const))),
  /** Secret inputs (stored in the server secret store, write-only). */
  credentialFields: Schema.Array(McpGatewayFieldSpec),
  /** Non-secret inputs (e.g. Atlassian site URL, Slack team ID). */
  configFields: Schema.Array(McpGatewayFieldSpec),
});
export type McpGatewayPluginDescriptor = typeof McpGatewayPluginDescriptor.Type;

export const McpGatewayPluginStatus = Schema.Literals([
  "active",
  "inactive",
  "awaiting-consent",
  "error",
  "unknown",
]);
export type McpGatewayPluginStatus = typeof McpGatewayPluginStatus.Type;

export const McpGatewayCredentialSource = Schema.Literals(["oauth", "manual"]);
export type McpGatewayCredentialSource = typeof McpGatewayCredentialSource.Type;

export const McpGatewayPluginState = Schema.Struct({
  pluginId: McpGatewayPluginId,
  /** Whether the plugin's server is enabled in the gateway registry. */
  enabled: Schema.Boolean,
  /** Whether credentials are stored locally (re-enable without re-entry). */
  hasCredentials: Schema.Boolean,
  /** How the stored credentials were obtained (absent when none). */
  credentialSource: Schema.optional(McpGatewayCredentialSource),
  /** Stored non-secret config values, echoed for display/edit. */
  configValues: Schema.Record(Schema.String, Schema.String),
  status: McpGatewayPluginStatus,
  detail: Schema.optional(Schema.String),
});
export type McpGatewayPluginState = typeof McpGatewayPluginState.Type;

export const McpGatewayStartOAuthInput = Schema.Struct({
  pluginId: McpGatewayPluginId,
});
export type McpGatewayStartOAuthInput = typeof McpGatewayStartOAuthInput.Type;

export const McpGatewayStartOAuthResult = Schema.Struct({
  /** Provider consent URL for the user's browser. */
  authorizationUrl: Schema.String,
  /** When the pending flow expires (UI poll timeout). */
  expiresAt: IsoDateTime,
});
export type McpGatewayStartOAuthResult = typeof McpGatewayStartOAuthResult.Type;

export const McpGatewayPluginListing = Schema.Struct({
  descriptor: McpGatewayPluginDescriptor,
  state: McpGatewayPluginState,
});
export type McpGatewayPluginListing = typeof McpGatewayPluginListing.Type;

export const McpGatewayStatus = Schema.Struct({
  /** Gateway + admin URLs and both tokens are present. */
  configured: Schema.Boolean,
  /** User toggle: inject the gateway endpoint into agent sessions. */
  enabled: Schema.Boolean,
  /** Result of the most recent health probe through the admin sidecar. */
  healthy: Schema.Boolean,
  gatewayUrl: TrimmedString,
  adminUrl: TrimmedString,
  hasGatewayToken: Schema.Boolean,
  hasAdminToken: Schema.Boolean,
  checkedAt: Schema.NullOr(IsoDateTime),
  lastError: Schema.optional(Schema.String),
});
export type McpGatewayStatus = typeof McpGatewayStatus.Type;

export const McpGatewaySetConnectionInput = Schema.Struct({
  url: TrimmedString,
  adminUrl: TrimmedString,
  enabled: Schema.Boolean,
  /** Write-only; omitted = keep the currently stored token. */
  gatewayToken: Schema.optional(TrimmedNonEmptyString),
  adminToken: Schema.optional(TrimmedNonEmptyString),
});
export type McpGatewaySetConnectionInput = typeof McpGatewaySetConnectionInput.Type;

export const McpGatewayActivatePluginInput = Schema.Struct({
  pluginId: McpGatewayPluginId,
  /** Keyed by `McpGatewayFieldSpec.key` of the descriptor's credentialFields. */
  credentials: Schema.Record(Schema.String, Schema.String),
  /** Keyed by `McpGatewayFieldSpec.key` of the descriptor's configFields. */
  config: Schema.Record(Schema.String, Schema.String).pipe(Schema.optionalKey),
});
export type McpGatewayActivatePluginInput = typeof McpGatewayActivatePluginInput.Type;

export const McpGatewayDeactivatePluginInput = Schema.Struct({
  pluginId: McpGatewayPluginId,
  /** Also remove stored credentials (default keeps them for re-enable). */
  forgetCredentials: Schema.Boolean,
});
export type McpGatewayDeactivatePluginInput = typeof McpGatewayDeactivatePluginInput.Type;

export class McpGatewayError extends Schema.TaggedErrorClass<McpGatewayError>()("McpGatewayError", {
  operation: Schema.String,
  detail: Schema.String,
  cause: Schema.optional(Schema.Defect()),
}) {
  override get message(): string {
    return `MCP gateway ${this.operation} failed: ${this.detail}`;
  }
}
